import type { Locale, RoomStatus } from "./types";

type Copy = {
  direction: "rtl" | "ltr";
  localeTag: string;
  primaryNavigation: string;
  heroTitle: string;
  heroBody: string;
  calendarConnected: string;
  calendarUpdated: string;
  scheduleTitle: string;
  scheduleSubtitle: string;
  room1: string;
  room2: string;
  bothRooms: string;
  time: string;
  availableWindows: string;
  roomWindows: string;
  updated: string;
  refresh: string;
  loading: string;
  retry: string;
  errorTitle: string;
  errorBody: string;
  emptyTitle: string;
  emptyBody: string;
  bookingHeroTitle: string;
  bookingHeroBody: string;
  bookingTitle: string;
  bookingSubtitle: string;
  flowGuideTitle: string;
  flowGuideBody: string;
  progressLabel: string;
  stepKind: string;
  stepKindHint: string;
  stepKindPrompt: string;
  kindSingle: string;
  kindSingleBody: string;
  kindCouple: string;
  kindCoupleBody: string;
  kindFourHands: string;
  kindFourHandsBody: string;
  oneRoom: string;
  twoRooms: string;
  twoTherapists: string;
  onlineReady: string;
  paymentLinkPending: string;
  stepTreatment: string;
  stepTreatmentHint: string;
  stepTreatmentPrompt: string;
  stepDate: string;
  stepDateHint: string;
  stepDatePrompt: string;
  chooseCalendarDate: string;
  dateWindowHint: string;
  dateOutsideWindow: string;
  nearbyDates: string;
  dateScheduleMissingTitle: string;
  dateScheduleMissingBody: string;
  stepTime: string;
  stepTimeHint: string;
  stepTimePrompt: string;
  stepDetails: string;
  stepDetailsHint: string;
  stepDetailsPrompt: string;
  minutes: string;
  totalPrice: string;
  minimumLeadTime: string;
  chooseKindFirst: string;
  chooseTreatmentFirst: string;
  chooseDateFirst: string;
  chooseRoomFirst: string;
  twoRoomsFromCalendar: string;
  busyTimesStayVisible: string;
  coupleSelectionHelp: string;
  fourHandsAvailability: string;
  legendTitle: string;
  unavailable: string;
  selected: string;
  selectThisRoom: string;
  selectAvailable: string;
  availableForDuration: string;
  tooSoon: string;
  minimumLeadShort: string;
  notEnoughContinuous: string;
  availableOnlyFor: string;
  notEnoughForDuration: string;
  selectBothRooms: string;
  selectedBothRooms: string;
  coupleUnavailable: string;
  coupleRoomBusy: string;
  coupleTooSoon: string;
  coupleNotEnoughContinuous: string;
  nameLabel: string;
  namePlaceholder: string;
  phoneLabel: string;
  phonePlaceholder: string;
  privacyTitle: string;
  privacyBody: string;
  previewBooking: string;
  completeFields: string;
  previewTitle: string;
  previewBody: string;
  kindSummary: string;
  serviceSummary: string;
  dateSummary: string;
  timeSummary: string;
  roomSummary: string;
  clientSummary: string;
  notBookedTitle: string;
  notBookedBody: string;
  bookingPolicyTitle: string;
  bookingPolicyBody: string;
  bookingPolicyConsent: string;
  bookingPolicyRequired: string;
  acceptPolicyToContinue: string;
  termsCancellation: string;
  privacyPolicy: string;
  accessibilityStatement: string;
  opensInNewTab: string;
  doublePaymentTitle: string;
  doublePaymentBody: string;
  openSecurePayment: string;
  paymentLinkRequired: string;
  checkingAvailability: string;
  availabilityChanged: string;
  availabilityCheckFailed: string;
  bookingSaveFailed: string;
  paymentUnavailable: string;
  editDetails: string;
  scheduleNav: string;
  bookingNav: string;
  savedSchedule: string;
  status: Record<RoomStatus, string>;
};

export const copy: Record<Locale, Copy> = {
  he: {
    direction: "rtl",
    localeTag: "he-IL",
    primaryNavigation: "ניווט ראשי",
    heroTitle: "רואים בדיוק מה פנוי.",
    heroBody: "כל שעה ושני חדרי הטיפולים מוצגים בנפרד, ישירות מיומן הקליניקה.",
    calendarConnected: "מחובר ליומן Google של הקליניקה",
    calendarUpdated: "הזמינות מתעדכנת אוטומטית",
    scheduleTitle: "זמינות שני החדרים",
    scheduleSubtitle: "פנוי, תפוס או סגור — לכל חדר בנפרד",
    room1: "חדר 1",
    room2: "חדר 2",
    bothRooms: "שני החדרים",
    time: "שעה",
    availableWindows: "חדרים פנויים",
    roomWindows: "כל חדר נספר בנפרד ביום שנבחר",
    updated: "עודכן",
    refresh: "רענון",
    loading: "טוענים את לוח החדרים…",
    retry: "לנסות שוב",
    errorTitle: "לא הצלחנו לטעון את הזמינות",
    errorBody:
      "לא מציגים שעות בלי נתוני יומן עדכניים. בדקו את החיבור ונסו שוב.",
    emptyTitle: "אין שעות להצגה",
    emptyBody: "ליום הזה עדיין לא התקבל לוח זמנים.",
    bookingHeroTitle: "מזמינים טיפול בחמישה צעדים ברורים.",
    bookingHeroBody: "בכל שלב ההסבר נמצא מיד ליד הכפתור שצריך ללחוץ עליו.",
    bookingTitle: "הזמנת טיפול",
    bookingSubtitle: "מתחילים בשלב 1 ומתקדמים לפי הסדר",
    flowGuideTitle: "איך מזמינים? ממשיכים מלמעלה למטה",
    flowGuideBody: "בחרו בכל שלב את האפשרות הרצויה. השלב הבא ייפתח אוטומטית.",
    progressLabel: "התקדמות ההזמנה",
    stepKind: "סוג ההזמנה",
    stepKindHint: "בחרו אם הטיפול מיועד ליחיד, לזוג או לעיסוי בארבע ידיים.",
    stepKindPrompt: "שלב 1 — לחצו על סוג הטיפול הרצוי",
    kindSingle: "טיפול ליחיד",
    kindSingleBody: "אדם אחד בחדר טיפולים אחד",
    kindCouple: "טיפול זוגי",
    kindCoupleBody: "שני אנשים בשני חדרים באותה שעה",
    kindFourHands: "עיסוי בארבע ידיים",
    kindFourHandsBody: "אדם אחד, שני מטפלים, חדר אחד",
    oneRoom: "חדר אחד",
    twoRooms: "שני חדרים",
    twoTherapists: "שני מטפלים",
    onlineReady: "תשלום מחובר",
    paymentLinkPending: "קישור תשלום יתווסף",
    stepTreatment: "משך הטיפול",
    stepTreatmentHint: "בחרו משך טיפול. המחיר המוצג הוא המחיר הכולל לסוג הטיפול שבחרתם.",
    stepTreatmentPrompt: "שלב 2 — לחצו על משך הטיפול הרצוי",
    stepDate: "תאריך",
    stepDateHint: "בחרו יום כדי לראות את המצב האמיתי של שני החדרים.",
    stepDatePrompt: "שלב 3 — בחרו תאריך בלוח השנה או באפשרויות שלמטה",
    chooseCalendarDate: "בחירת תאריך בלוח השנה",
    dateWindowHint: "אפשר לבחור תאריך עד {days} יום קדימה, עד {date}. זמינות החדרים נבדקת לפי התאריך.",
    dateOutsideWindow: "בחרו תאריך מהיום ועד {days} יום קדימה.",
    nearbyDates: "תאריכים קרובים",
    dateScheduleMissingTitle: "עדיין אין נתוני זמינות לתאריך הזה",
    dateScheduleMissingBody: "לא ניתן לבחור שעה בלי מידע מהיומן. בחרו תאריך אחר או רעננו את הזמינות.",
    stepTime: "שעה וחדר",
    stepTimeHint: "כל שורה מציגה שעה אחת ואת חדר 1 וחדר 2 בנפרד.",
    stepTimePrompt: "שלב 4 — לחצו על האפשרות הפנויה בשעה הרצויה",
    stepDetails: "פרטי קשר",
    stepDetailsHint: "הקליניקה תשתמש בפרטים כדי לזהות את ההזמנה.",
    stepDetailsPrompt: "שלב 5 — הקלידו שם מלא ומספר טלפון",
    minutes: "דקות",
    totalPrice: "מחיר כולל",
    minimumLeadTime: "אפשר להזמין רק זמן שמתחיל בעוד שעתיים לפחות.",
    chooseKindFirst: "כדי לפתוח את השלב הזה, בחרו קודם סוג הזמנה בשלב 1.",
    chooseTreatmentFirst: "כדי לפתוח את השלב הזה, בחרו קודם משך טיפול בשלב 2.",
    chooseDateFirst: "כדי לראות את שני החדרים, בחרו קודם תאריך בשלב 3.",
    chooseRoomFirst: "כדי למלא פרטים, בחרו קודם שעה פנויה בשלב 4.",
    twoRoomsFromCalendar: "הנתונים מגיעים ישירות מיומן שני חדרי הטיפולים",
    busyTimesStayVisible:
      "כל תא מסביר בדיוק מה קורה: פנוי, תפוס, קרוב מדי להזמנה או ללא מספיק זמן רצוף.",
    coupleSelectionHelp:
      "לטיפול זוגי אפשר לבחור שעה רק כאשר שני החדרים פנויים לכל משך הטיפול.",
    fourHandsAvailability:
      "לעיסוי בארבע ידיים נדרשים חדר אחד ושני מטפלים. היומן מציג את זמינות החדר בלבד; יש לאשר עם הקליניקה ששני מטפלים זמינים לפני תשלום.",
    legendTitle: "מקרא מצב החדרים",
    unavailable: "לא ניתן להזמין",
    selected: "נבחר",
    selectThisRoom: "בחירת החדר",
    selectAvailable: "פנוי — לחצו לבחירה",
    availableForDuration: "פנוי לכל {duration} הדקות",
    tooSoon: "קרוב מדי להזמנה",
    minimumLeadShort: "יש להזמין לפחות שעתיים מראש",
    notEnoughContinuous: "אין מספיק זמן רצוף",
    availableOnlyFor: "פנוי רק ל־{minutes} דקות ברצף",
    notEnoughForDuration: "לא פנוי לכל {duration} הדקות",
    selectBothRooms: "בחירת השעה לזוג",
    selectedBothRooms: "השעה נבחרה לזוג",
    coupleUnavailable: "לא פנוי לזוג",
    coupleRoomBusy: "לפחות חדר אחד תפוס",
    coupleTooSoon: "יש להזמין לפחות שעתיים מראש",
    coupleNotEnoughContinuous: "שני החדרים אינם פנויים ברצף ל־{duration} דקות",
    nameLabel: "שם מלא",
    namePlaceholder: "לדוגמה: דנה כהן",
    phoneLabel: "מספר טלפון",
    phonePlaceholder: "050-000-0000",
    privacyTitle: "הפרטים נשמרים לצורך זיהוי ההזמנה",
    privacyBody:
      "לפני המעבר לקופה, התור יופיע ביומן כ\"ממתין לבדיקת תשלום\" כדי שהמנהלים יוכלו לבדוק אותו.",
    previewBooking: "בדיקת הפרטים לפני תשלום",
    completeFields: "השלימו את חמשת השלבים כדי להמשיך",
    previewTitle: "סיכום לפני תשלום",
    previewBody: "בדקו את סוג הטיפול, התאריך, השעה והחדרים שנבחרו.",
    kindSummary: "סוג הזמנה",
    serviceSummary: "משך ומחיר",
    dateSummary: "תאריך",
    timeSummary: "שעה",
    roomSummary: "חדר",
    clientSummary: "לקוח/ה",
    notBookedTitle: "התור יישמר ביומן לפני המעבר לתשלום",
    notBookedBody:
      "ביומן יופיעו סוג הטיפול, השעה, השם והטלפון עם הסטטוס \"ממתין לבדיקת תשלום\". המנהלים יאמתו את התשלום באתר הקופה.",
    bookingPolicyTitle: "מדיניות הזמנה, איחור וביטול",
    bookingPolicyBody:
      "זמן הטיפול נשמר במיוחד עבורכם. במקרה של איחור, זמן האיחור ינוכה ממשך הטיפול והטיפול יסתיים בשעה שנקבעה מראש. תנאי הביטול המלאים מופיעים בקישור שלמטה.",
    bookingPolicyConsent:
      "קראתי והבנתי את מדיניות ההזמנה, האיחור והביטול, ואני מאשר/ת אותה.",
    bookingPolicyRequired: "יש לאשר את המדיניות לפני המעבר לתשלום.",
    acceptPolicyToContinue: "אשרו את מדיניות ההזמנה והביטול כדי להמשיך",
    termsCancellation: "תנאי שימוש וביטולים",
    privacyPolicy: "מדיניות פרטיות",
    accessibilityStatement: "הצהרת נגישות",
    opensInNewTab: "נפתח בחלון חדש",
    doublePaymentTitle: "העיצוב מוכן; קישור התשלום הכפול עדיין חסר",
    doublePaymentBody:
      "לא נפתח קופה לטיפול זוגי או לעיסוי בארבע ידיים עד שיתקבל קישור GotPose מתאים למחיר הכפול.",
    openSecurePayment: "שמירת התור והמשך לקופה",
    paymentLinkRequired: "נדרש קישור תשלום כדי להמשיך",
    checkingAvailability: "שומרים את התור ובודקים זמינות…",
    availabilityChanged:
      "הזמן שבחרתם כבר אינו פנוי. בחרו שעה אחרת; הקופה לא נפתחה.",
    availabilityCheckFailed:
      "לא הצלחנו לבדוק שוב את היומן. נסו שוב; הקופה לא נפתחה.",
    bookingSaveFailed:
      "לא הצלחנו לשמור את התור ביומן. הקופה לא נפתחה. נסו שוב או פנו לקליניקה.",
    paymentUnavailable: "קישור התשלום לסוג טיפול זה עדיין לא חובר.",
    editDetails: "חזרה לעריכה",
    scheduleNav: "לוח שני החדרים",
    bookingNav: "הזמנת טיפול",
    savedSchedule: "מוצג העותק האחרון שנשמר",
    status: {
      free: "פנוי",
      busy: "תפוס",
      closed: "סגור",
    },
  },
  en: {
    direction: "ltr",
    localeTag: "en-GB",
    primaryNavigation: "Primary navigation",
    heroTitle: "See exactly what is available.",
    heroBody:
      "Every hour and both treatment rooms are shown separately, directly from the clinic calendar.",
    calendarConnected: "Connected to the clinic Google Calendar",
    calendarUpdated: "Availability refreshes automatically",
    scheduleTitle: "Both rooms at a glance",
    scheduleSubtitle: "Available, busy or closed — separately for each room",
    room1: "Room 1",
    room2: "Room 2",
    bothRooms: "Both rooms",
    time: "Time",
    availableWindows: "available rooms",
    roomWindows: "each room is counted separately on the selected day",
    updated: "Updated",
    refresh: "Refresh",
    loading: "Loading the room schedule…",
    retry: "Try again",
    errorTitle: "We couldn’t load availability",
    errorBody:
      "We do not show times without current calendar data. Check your connection and try again.",
    emptyTitle: "No times to display",
    emptyBody: "No schedule is available for this day yet.",
    bookingHeroTitle: "Book a treatment in five clear steps.",
    bookingHeroBody:
      "At every step, the explanation sits beside the button you need to press.",
    bookingTitle: "Book a treatment",
    bookingSubtitle: "Start at step 1 and continue in order",
    flowGuideTitle: "How to book: move from top to bottom",
    flowGuideBody:
      "Choose one option at each step. The next step opens automatically.",
    progressLabel: "Booking progress",
    stepKind: "Booking type",
    stepKindHint:
      "Choose a treatment for one, a couples treatment or a four-hands massage.",
    stepKindPrompt: "Step 1 — tap the treatment type you want",
    kindSingle: "Treatment for one",
    kindSingleBody: "One guest in one treatment room",
    kindCouple: "Couples treatment",
    kindCoupleBody: "Two guests in two rooms at the same time",
    kindFourHands: "Four-hands massage",
    kindFourHandsBody: "One guest, two therapists, one room",
    oneRoom: "One room",
    twoRooms: "Two rooms",
    twoTherapists: "Two therapists",
    onlineReady: "Checkout connected",
    paymentLinkPending: "Checkout link to be added",
    stepTreatment: "Treatment length",
    stepTreatmentHint:
      "Choose a duration. The price shown is the total for the treatment type you selected.",
    stepTreatmentPrompt: "Step 2 — tap the treatment length you want",
    stepDate: "Date",
    stepDateHint: "Choose a day to see the real status of both rooms.",
    stepDatePrompt: "Step 3 — choose a date in the calendar or from the options below",
    chooseCalendarDate: "Choose a date in the calendar",
    dateWindowHint: "Choose up to {days} days ahead, through {date}. Room availability is checked for the date you select.",
    dateOutsideWindow: "Choose a date from today through the next {days} days.",
    nearbyDates: "Nearby dates",
    dateScheduleMissingTitle: "Availability for this date is not available yet",
    dateScheduleMissingBody: "A time cannot be selected without calendar data. Choose another date or refresh availability.",
    stepTime: "Time and room",
    stepTimeHint: "Every row shows one time, Room 1 and Room 2 separately.",
    stepTimePrompt: "Step 4 — tap an available option at your preferred time",
    stepDetails: "Contact details",
    stepDetailsHint: "The clinic uses these details to identify the booking.",
    stepDetailsPrompt: "Step 5 — enter your full name and phone number",
    minutes: "minutes",
    totalPrice: "Total price",
    minimumLeadTime: "Bookings must start at least two hours from now.",
    chooseKindFirst: "Choose a booking type in step 1 to unlock this step.",
    chooseTreatmentFirst:
      "Choose a treatment length in step 2 to unlock this step.",
    chooseDateFirst: "Choose a date in step 3 to see both rooms.",
    chooseRoomFirst:
      "Choose an available time in step 4 before entering details.",
    twoRoomsFromCalendar:
      "Availability comes directly from the two-room clinic calendar",
    busyTimesStayVisible:
      "Every cell gives the exact reason: available, busy, too soon to book, or not enough continuous time.",
    coupleSelectionHelp:
      "A couples treatment can be selected only when both rooms stay available for the full treatment.",
    fourHandsAvailability:
      "Four-hands massage needs one room and two therapists. This calendar checks the room only; confirm that two therapists are available with the clinic before paying.",
    legendTitle: "Room-status legend",
    unavailable: "Cannot be booked",
    selected: "Selected",
    selectThisRoom: "Select this room",
    selectAvailable: "Available — tap to select",
    availableForDuration: "Available for all {duration} minutes",
    tooSoon: "Too soon to book",
    minimumLeadShort: "Book at least two hours in advance",
    notEnoughContinuous: "Not enough continuous time",
    availableOnlyFor: "Available for only {minutes} continuous minutes",
    notEnoughForDuration: "Not available for all {duration} minutes",
    selectBothRooms: "Select this time for two",
    selectedBothRooms: "Time selected for two",
    coupleUnavailable: "Not available for two",
    coupleRoomBusy: "At least one room is busy",
    coupleTooSoon: "Book at least two hours in advance",
    coupleNotEnoughContinuous:
      "Both rooms are not continuously available for {duration} minutes",
    nameLabel: "Full name",
    namePlaceholder: "For example: Dana Cohen",
    phoneLabel: "Phone number",
    phonePlaceholder: "050-000-0000",
    privacyTitle: "Details are stored to identify the booking",
    privacyBody:
      "Before checkout opens, the appointment appears in the calendar as pending payment verification for the administrators.",
    previewBooking: "Review details before payment",
    completeFields: "Complete all five steps to continue",
    previewTitle: "Summary before payment",
    previewBody: "Check the treatment type, date, time and selected rooms.",
    kindSummary: "Booking type",
    serviceSummary: "Length and price",
    dateSummary: "Date",
    timeSummary: "Time",
    roomSummary: "Room",
    clientSummary: "Client",
    notBookedTitle: "The appointment is saved before checkout opens",
    notBookedBody:
      "The calendar shows the treatment, time, name and phone as pending payment verification. The administrators verify payment in the checkout system.",
    bookingPolicyTitle: "Booking, late-arrival and cancellation policy",
    bookingPolicyBody:
      "Your treatment time is reserved especially for you. If you arrive late, the late time is deducted from your treatment and the appointment still ends at the originally scheduled time. Full cancellation terms are available through the link below.",
    bookingPolicyConsent:
      "I have read, understood and agree to the booking, late-arrival and cancellation policy.",
    bookingPolicyRequired: "You must accept the policy before continuing to payment.",
    acceptPolicyToContinue: "Accept the booking and cancellation policy to continue",
    termsCancellation: "Terms & cancellations",
    privacyPolicy: "Privacy policy",
    accessibilityStatement: "Accessibility statement",
    opensInNewTab: "opens in a new tab",
    doublePaymentTitle:
      "The design is ready; the double-price checkout link is still missing",
    doublePaymentBody:
      "Checkout stays disabled for couples and four-hands treatments until a matching GotPose link is provided.",
    openSecurePayment: "Save appointment and continue to checkout",
    paymentLinkRequired: "A checkout link is required to continue",
    checkingAvailability: "Saving the appointment and checking availability…",
    availabilityChanged:
      "The selected time is no longer available. Choose another time; checkout was not opened.",
    availabilityCheckFailed:
      "We could not recheck the calendar. Try again; checkout was not opened.",
    bookingSaveFailed:
      "We could not save the appointment in the calendar. Checkout was not opened. Try again or contact the clinic.",
    paymentUnavailable:
      "The checkout link for this treatment type is not connected yet.",
    editDetails: "Back to editing",
    scheduleNav: "Two-room schedule",
    bookingNav: "Book treatment",
    savedSchedule: "Showing the last saved copy",
    status: {
      free: "Available",
      busy: "Busy",
      closed: "Closed",
    },
  },
};
